import * as React from "react";
import { getBlockEntry } from "./registry";
/**
 * A stable key for one request.
 *
 * Two blocks asking for the same entries — a news grid and a featured panel on
 * one page, both showing the latest three — must share one fetch and one cache
 * entry. Built from the request's own fields in a fixed order, so the key does
 * not change when an unrelated part of the block does.
 */
export function requestKey(request) {
    const filters = (request.filters ?? [])
        .map((f) => `${f.field}:${f.op}:${f.value}`)
        .sort()
        .join(",");
    return [
        request.kind,
        request.collectionId,
        request.sort ?? "",
        request.dir ?? "",
        request.limit ?? "",
        filters,
    ].join("|");
}
export const EMPTY_BLOCK_DATA = {
    collections: {},
    entries: {},
    status: {},
};
const LoadEntriesContext = React.createContext(undefined);
const BlockDataContext = React.createContext(EMPTY_BLOCK_DATA);
export function BlockDataProvider({ value, children, loadEntries, }) {
    return React.createElement(BlockDataContext.Provider, { value }, React.createElement(LoadEntriesContext.Provider, { value: loadEntries }, children));
}
/**
 * What one block asked for, and how that went.
 *
 * A block with no request — or one naming a collection that no longer exists —
 * gets an empty list and a `ready` status. That is deliberate: a stale
 * collectionId is a normal consequence of deleting a collection, and a page
 * that threw on one would be a page an Editor could destroy by accident from a
 * different screen.
 */
export function useBlockData(request) {
    const data = React.useContext(BlockDataContext);
    const loadEntries = React.useContext(LoadEntriesContext);
    if (!request || !request.collectionId) {
        return { entries: [], collection: null, status: "ready" };
    }
    const key = requestKey(request);
    return {
        loadEntries,
        nextOffset: data.nextOffsets?.[key],
        entries: data.entries[key] ?? [],
        collection: data.collections[request.collectionId] ?? null,
        status: data.status[key] ?? "ready",
    };
}
/**
 * Every request the blocks on a page make.
 *
 * Scans through the REGISTRY rather than a list of block ids, so a block that
 * declares `dataRequests` is picked up by having been registered — there is no
 * second list to keep in step, and forgetting one would show as an empty list
 * rather than as an error.
 *
 * Deduplicated by key, so two blocks wanting the same entries cost one fetch.
 */
export function collectDataRequests(blocks) {
    const byKey = new Map();
    for (const block of blocks) {
        const entry = getBlockEntry(block.componentId);
        if (!entry?.dataRequests)
            continue;
        for (const request of entry.dataRequests(block)) {
            if (!request.collectionId)
                continue;
            byKey.set(requestKey(request), request);
        }
    }
    return [...byKey.values()];
}
//# sourceMappingURL=data.js.map